import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  CreditCard,
  FileWarning,
  Receipt,
  TrendingUp,
  Undo2,
} from "lucide-react";
import { formatMoney, FISCAL_DOCUMENT_STATUS_LABELS, FISCAL_DOCUMENT_STATUS_TONE } from "@finora/config";
import { StatusBadge, Skeleton, EmptyState, Button } from "@finora/ui";
import type { FiscalDocumentDto } from "@finora/types";
import { api, apiFetch } from "../../lib/api";
import { randomUUID } from "../../lib/uuid";
import { ReceiptView } from "../Pos/ReceiptView";

// ---------------------------------------------------------------------------
// Tip podataka sa API-ja proširen novim (opcionim) poljima koja mock dizajn
// zahteva, a koja trenutni `api.getDashboard()` možda još uvek ne vraća:
// bankImportTotal, cashRegisterTotal, currentMonthRevenueChangePct,
// outstandingInvoicesCount, todayFiscalDocumentsCount. Dok backend ne bude
// ažuriran, sve se bezbedno svode na razumne fallback vrednosti (0 / bez
// prikaza trenda) - ništa se ne ruši ako polja nedostaju.
// ---------------------------------------------------------------------------
type DashboardData = Awaited<ReturnType<typeof api.getDashboard>>;
type DashboardDataWithExtras = DashboardData & {
  bankImportTotal?: number;
  cashRegisterTotal?: number;
  additionalChargesTotal?: number;
  currentMonthRevenueChangePct?: number;
  outstandingInvoicesCount?: number;
  todayFiscalDocumentsCount?: number;
};

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Dobro jutro";
  if (hour < 18) return "Dobar dan";
  return "Dobro veče";
}

function formatDateSr(date: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}.`;
}

function formatCompactAmount(value: number) {
  if (value >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
  if (value >= 1_000) return `${Math.round(value / 1000)}k`;
  return `${Math.round(value)}`;
}

function getNiceAxisMax(values: number[]) {
  const max = Math.max(1, ...values);
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  return Math.ceil(max / magnitude) * magnitude;
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

const DATE_RANGE_PRESETS = [
  { label: "Ovaj mesec", getRange: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  {
    label: "Prošli mesec",
    getRange: () => {
      const start = startOfMonth(new Date());
      start.setMonth(start.getMonth() - 1);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      return { start, end };
    },
  },
  {
    label: "Poslednjih 30 dana",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return { start, end };
    },
  },
];

function SummaryCard({
  icon: Icon,
  iconClassName,
  label,
  value,
  valueClassName,
  description,
  descriptionClassName,
}: {
  icon: LucideIcon;
  iconClassName: string;
  label: string;
  value: string;
  valueClassName?: string;
  description?: string;
  descriptionClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
      <p className={`mt-3 text-2xl font-semibold ${valueClassName ?? "text-slate-900"}`}>{value}</p>
      {description ? (
        <p className={`mt-1 text-xs ${descriptionClassName ?? "text-slate-400"}`}>{description}</p>
      ) : null}
    </div>
  );
}

function AttentionRow({
  icon: Icon,
  tone,
  label,
  count,
}: {
  icon: LucideIcon;
  tone: "rose" | "amber";
  label: string;
  count: number;
}) {
  const toneClasses = tone === "rose" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700";
  const badgeClasses = tone === "rose" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700";
  return (
    <a
      href="#"
      className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors hover:opacity-90 ${toneClasses}`}
    >
      <span className="flex items-center gap-2 font-medium">
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </span>
      <span className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClasses}`}>{count}</span>
        <ChevronRight className="h-4 w-4 opacity-60" aria-hidden />
      </span>
    </a>
  );
}

function DateRangePicker({
  onChange,
}: {
  /** Prosleđuje IZABRANI period nazad na DashboardPage (razgovor 28.09.2026,
   * "period picker treba da menja i podatke") - do sada je ovaj komponent
   * imao SOPSTVENI lokalni state i menjao SAMO prikazani label, upit ka
   * API-ju je uvek ćutke ostajao na tekućem mesecu. */
  onChange?: (range: { start: Date; end: Date }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(() => ({ start: startOfMonth(new Date()), end: new Date() }));

  const label = `${formatDateSr(range.start)} - ${formatDateSr(range.end)}`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-slate-600 shadow-subtle hover:bg-slate-50"
      >
        <Calendar className="h-4 w-4 text-slate-400" aria-hidden />
        {label}
        <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
      </button>
      {open ? (
        <div className="absolute right-0 z-10 mt-2 w-48 overflow-hidden rounded-lg border border-border bg-surface shadow-subtle">
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => {
                const next = preset.getRange();
                setRange(next);
                setOpen(false);
                onChange?.(next);
              }}
              className="block w-full px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** "Fiskalizovani Avans" umesto generičkog "Fiskalizovano" za avansne račune
 * (doc.isAdvance / doc.pfrDocumentType === "AVANS") - razgovor 28.09.2026,
 * "Poslednji fiskalni računi" tabela do sada nije razlikovala avans od
 * običnog prometa u koloni Status. Ne dira se STATUS (i dalje FISCALIZED),
 * samo prikazani tekst za ovaj slučaj. */
function statusLabelFor(doc: FiscalDocumentDto): string {
  if (doc.status === "FISCALIZED" && (doc.isAdvance || doc.pfrDocumentType === "AVANS")) {
    return "Fiskalizovani Avans";
  }
  return FISCAL_DOCUMENT_STATUS_LABELS[doc.status] ?? doc.status;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewingDoc, setViewingDoc] = useState<FiscalDocumentDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Period picker (razgovor 28.09.2026) - prosleđuje se kao "from"/"to" ka
  // GET /dashboard, koji sada opciono prima taj period (vidi
  // DashboardService.getSummary/DashboardController) - podrazumevano
  // ostaje "Ovaj mesec" ako se ništa ne izabere, isto kao ranije.
  const [range, setRange] = useState<{ start: Date; end: Date }>(() => ({
    start: startOfMonth(new Date()),
    end: new Date(),
  }));

  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

  // `from`/`to` se namerno šalje preko `apiFetch` (mali helper u lib/api.ts),
  // ne preko `api.getDashboard()` - izbegava se dirati veliki, deljeni
  // @finora/api-client paket za ovaj zadatak (vidi napomenu u PR opisu).
  // Backend endpoint (GET /dashboard) je isti, sada prima opcione from/to.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard", toIsoDate(range.start), toIsoDate(range.end)],
    queryFn: () =>
      apiFetch<DashboardData>(
        `/dashboard?from=${toIsoDate(range.start)}&to=${toIsoDate(range.end)}`,
      ),
  });
  const d = data as DashboardDataWithExtras | undefined;

  const { data: myPackage } = useQuery({
    queryKey: ["my-package"],
    queryFn: () => api.getMyPackage(),
  });
  const { data: myInvoices } = useQuery({
    queryKey: ["my-invoices"],
    queryFn: () => api.getMyInvoices(),
  });
  // "Dodatna zaduženja" - nema jedinstveno polje za ovo u bazi (vidi
  // razgovor 28.09.2026); najbliži postojeći koncept je ukupan NEPLAĆEN
  // iznos ("Za uplatu") preko dokumenata koje firmi izdaje Finora
  // (api.getMyInvoices() - ista lista kao na "Moji računi"), što uključuje i
  // sam paket i sve dodatke/kupovine preko njega - vidi napomenu u
  // finalnom izveštaju o ovoj pretpostavci.
  const additionalCharges = useMemo(
    () => (myInvoices?.documents ?? []).reduce((sum, doc) => sum + Number(doc.dueAmount), 0),
    [myInvoices],
  );

  const copyIdempotencyKey = () => randomUUID();

  const refund = useMutation({
    mutationFn: (id: string) => api.refundFiscalDocument(id, copyIdempotencyKey()),
    onSuccess: (updated) => {
      if (updated.status !== "REFUNDED") {
        setActionError(updated.fiscalizationAttempts.at(-1)?.providerMessage ?? "Storno nije uspeo.");
      } else {
        setActionError(null);
      }
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : "Storno nije uspeo.");
    },
  });

  const chartMax = useMemo(() => {
    if (!d?.invoicedVsCollected?.length) return 1;
    return getNiceAxisMax(d.invoicedVsCollected.flatMap((r) => [Number(r.invoiced), Number(r.collected)]));
  }, [d?.invoicedVsCollected]);

  const axisTicks = useMemo(() => {
    const steps = 5;
    return Array.from({ length: steps + 1 }, (_, i) => chartMax - (chartMax / steps) * i);
  }, [chartMax]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{getGreeting()} 👋</h1>
          <p className="text-sm text-slate-500">Pregled poslovanja za tekući mesec</p>
        </div>
        <DateRangePicker onChange={setRange} />
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-20 w-full rounded-xl" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        </div>
      ) : isError || !d ? (
        <EmptyState
          icon={AlertTriangle}
          title="Ne mogu da učitam podatke sa API-ja"
          description="Proverite da li je API server pokrenut (pnpm dev)."
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Vaše trenutno stanje</p>
              <p className="mt-1 text-sm text-slate-500">
                Uvoz bankovnih izvoda:{" "}
                <span className="font-medium text-slate-700">{formatMoney(d.bankImportTotal ?? 0)}</span>
                <span className="mx-2 text-slate-300">|</span>
                Kasa (osnovna):{" "}
                <span className="font-medium text-slate-700">{formatMoney(d.cashRegisterTotal ?? 0)}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-slate-400">Ukupno ovog meseca</p>
                <p className="text-sm font-semibold text-slate-900">
                  {formatMoney((d.bankImportTotal ?? 0) + (d.cashRegisterTotal ?? 0))}
                </p>
              </div>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                <BarChart3 className="h-5 w-5" aria-hidden />
              </span>
            </div>
          </div>

          {/* "Vaše trenutno stanje" - paket/pretplata (razgovor 28.09.2026,
              Task 2). Namerno odvojen blok od "Vaše trenutno stanje" iznad
              (uvoz izvoda/kasa) - obe kartice nose isti naslov po
              zahtevu, iako prikazuju različite stvari; ako to bude
              zbunjujuće u praksi, jedno od dva imena treba promeniti (vidi
              napomena u PR opisu). Podaci dolaze sa GET /company/subscription
              (isti izvor kao "Moj paket" stranica), "Dodatna zaduženja" je
              najbliža dostupna aproksimacija - vidi napomenu uz
              `additionalCharges` iznad. */}
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-4 shadow-subtle sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Vaše trenutno stanje</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-500">
                <span>
                  Vaš paket:{" "}
                  <span className="font-medium text-slate-700">
                    {myPackage?.current?.packageName ?? "Nema aktivan paket"}
                  </span>
                </span>
                <span>
                  Mesečna pretplata:{" "}
                  <span className="font-medium text-slate-700">
                    {myPackage?.current ? formatMoney(myPackage.current.priceGross) : "—"}
                  </span>
                </span>
                <span>
                  Dodatna zaduženja:{" "}
                  <span className="font-medium text-slate-700">{formatMoney(d.additionalChargesTotal ?? additionalCharges)}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                <CreditCard className="h-5 w-5" aria-hidden />
              </span>
              <Button variant="secondary" onClick={() => navigate("/moj-paket")}>
                Moj paket
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={TrendingUp}
              iconClassName="bg-emerald-50 text-emerald-600"
              label="Prihod ovog meseca"
              value={formatMoney(d.currentMonthRevenue)}
              valueClassName="text-emerald-600"
              description={
                d.currentMonthRevenueChangePct != null
                  ? `↑ ${d.currentMonthRevenueChangePct > 0 ? "+" : ""}${d.currentMonthRevenueChangePct}% u odnosu na prošli mesec`
                  : undefined
              }
              descriptionClassName="text-emerald-600"
            />
            <SummaryCard
              icon={Clock}
              iconClassName="bg-amber-50 text-amber-600"
              label="Za naplatu"
              value={formatMoney(d.outstandingAmount)}
              valueClassName="text-amber-600"
              description={
                d.outstandingInvoicesCount != null
                  ? `${d.outstandingInvoicesCount} neplaćenih faktura`
                  : undefined
              }
            />
            <SummaryCard
              icon={CheckCircle2}
              iconClassName="bg-sky-50 text-sky-600"
              label="Dospela potraživanja"
              value={formatMoney(d.overdueAmount)}
              description={Number(d.overdueAmount) === 0 ? "Sve je ažurirano" : "Zahteva pažnju"}
            />
            <SummaryCard
              icon={Receipt}
              iconClassName="bg-violet-50 text-violet-600"
              label="Fiskalni promet danas"
              value={formatMoney(d.todayFiscalTurnover)}
              description={
                d.todayFiscalDocumentsCount != null
                  ? `${d.todayFiscalDocumentsCount} fiskalnih računa`
                  : undefined
              }
            />
          </div>

          {d.bankMonthly && (d.bankYearIncome ?? 0) + (d.bankYearExpense ?? 0) > 0 && (
            <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-sm font-medium text-slate-700">Prihod ove godine (iz izvoda)</h2>
                  <p className="text-2xl font-semibold text-emerald-600">{formatMoney(String(d.bankYearIncome ?? 0))}</p>
                  <p className="text-xs text-slate-400">
                    Izlaz: {formatMoney(String(d.bankYearExpense ?? 0))} · uvezeni izvodi {d.bankYear}.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
                    Ulaz
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-rose-400" aria-hidden />
                    Izlaz
                  </span>
                </div>
              </div>
              {(() => {
                const max = getNiceAxisMax(d.bankMonthly.flatMap((m) => [m.income, m.expense]));
                return (
                  <div className="flex h-40 items-end gap-2">
                    {d.bankMonthly.map((m) => (
                      <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
                        <div className="flex h-32 w-full items-end gap-0.5">
                          <div
                            className="flex-1 rounded-t bg-emerald-500"
                            style={{ height: `${Math.max((m.income / max) * 100, m.income > 0 ? 2 : 0)}%` }}
                            title={`Ulaz: ${formatMoney(String(m.income))}`}
                          />
                          <div
                            className="flex-1 rounded-t bg-rose-400"
                            style={{ height: `${Math.max((m.expense / max) * 100, m.expense > 0 ? 2 : 0)}%` }}
                            title={`Izlaz: ${formatMoney(String(m.expense))}`}
                          />
                        </div>
                        <span className="text-xs text-slate-400">{m.month}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle lg:col-span-2">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-brand" aria-hidden />
                  <h2 className="text-sm font-medium text-slate-700">Fakturisano naspram naplaćeno</h2>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-brand-light" aria-hidden />
                    Fakturisano
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-brand" aria-hidden />
                    Naplaćeno
                  </span>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Poslednjih 6 meseci
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </div>

              {!d.invoicedVsCollected?.length ? (
                <EmptyState icon={TrendingUp} title="Nema podataka za prikaz" />
              ) : (
                <div className="flex h-48 gap-3">
                  <div className="flex h-36 flex-col justify-between text-right text-xs text-slate-400">
                    {axisTicks.map((tick) => (
                      <span key={tick}>{formatCompactAmount(tick)}</span>
                    ))}
                  </div>
                  <div className="relative flex flex-1 items-end gap-3">
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex h-36 flex-col justify-between">
                      {axisTicks.map((tick) => (
                        <div key={tick} className="border-t border-slate-100" />
                      ))}
                    </div>
                    {d.invoicedVsCollected.map((row) => {
                      const invoicedHeight = Math.max((Number(row.invoiced) / chartMax) * 100, 2);
                      const collectedHeight = Math.max((Number(row.collected) / chartMax) * 100, 2);
                      return (
                        <div key={row.month} className="flex flex-1 flex-col items-center gap-2">
                          <div className="flex h-36 w-full items-end gap-1">
                            <div
                              className="flex-1 rounded-t bg-brand-light"
                              style={{ height: `${invoicedHeight}%` }}
                              title={`Fakturisano: ${formatMoney(row.invoiced)}`}
                            />
                            <div
                              className="flex-1 rounded-t bg-brand"
                              style={{ height: `${collectedHeight}%` }}
                              title={`Naplaćeno: ${formatMoney(row.collected)}`}
                            />
                          </div>
                          <span className="text-xs text-slate-400">{row.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-medium text-slate-700">Potrebna pažnja</h2>
                <button
                  type="button"
                  onClick={() => navigate("/efakture")}
                  className="text-xs font-medium text-brand hover:underline"
                >
                  Vidi sve
                </button>
              </div>
              {d.eInvoicesPendingDecision === 0 && d.documentsNeedingAttention === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Nema stavki koje zahtevaju pažnju.</p>
              ) : (
                <div className="space-y-2">
                  {d.eInvoicesPendingDecision > 0 ? (
                    <AttentionRow
                      icon={FileWarning}
                      tone="rose"
                      label="eFakture koje čekaju odluku"
                      count={d.eInvoicesPendingDecision}
                    />
                  ) : null}
                  {d.documentsNeedingAttention > 0 ? (
                    <AttentionRow
                      icon={Clock}
                      tone="amber"
                      label="Dokumenti koji zahtevaju pažnju"
                      count={d.documentsNeedingAttention}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {actionError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</p>
          )}

          <div className="rounded-xl border border-border bg-surface shadow-subtle">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="text-sm font-medium text-slate-700">Poslednji fiskalni računi</h2>
              <button
                type="button"
                onClick={() => navigate("/fiskalizacija")}
                className="text-xs font-medium text-brand hover:underline"
              >
                Vidi sve
              </button>
            </div>
            {d.recentInvoices.length === 0 ? (
              <div className="p-5">
                <EmptyState icon={AlertTriangle} title="Još nema izdatih računa" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs font-medium uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-3 font-medium">Broj</th>
                      <th className="px-5 py-3 font-medium">Datum</th>
                      <th className="px-5 py-3 font-medium">Iznos</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 text-right font-medium">Akcije</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {d.recentInvoices.map((doc) => (
                      <tr key={doc.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800">{doc.documentNumber ?? "—"}</td>
                        <td className="px-5 py-3 text-slate-500">
                          {new Date(doc.createdAt).toLocaleDateString("sr-RS")}
                        </td>
                        <td className="px-5 py-3 text-slate-700">{formatMoney(doc.grossAmount, doc.currency)}</td>
                        <td className="px-5 py-3">
                          <StatusBadge
                            label={statusLabelFor(doc)}
                            tone={FISCAL_DOCUMENT_STATUS_TONE[doc.status]}
                          />
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setViewingDoc(doc)}
                              className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                            >
                              Pogledaj
                            </button>
                            {/* "Kopija računa" je već dostupna UNUTAR
                                "Pogledaj" modala (ReceiptView sa
                                allowCopyPrint - dugme "Odštampaj kopiju",
                                koje interno zove isti PFR KOPIJA poziv kao
                                Fiskalizacija -> Poslednji računi). Storno je
                                izdvojen ovde kao posebna akcija, isti
                                obrazac/potvrda kao na
                                FiscalDocumentsListPage.tsx. */}
                            {doc.status === "FISCALIZED" && (
                              <button
                                type="button"
                                title="Storno"
                                aria-label="Storniraj račun"
                                onClick={() => {
                                  if (window.confirm("Stornirati (refundirati) ovaj račun?")) {
                                    setActionError(null);
                                    refund.mutate(doc.id);
                                  }
                                }}
                                disabled={refund.isPending}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                              >
                                <Undo2 className="h-4 w-4" aria-hidden />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {viewingDoc && (
        <ReceiptView document={viewingDoc} onClose={() => setViewingDoc(null)} allowCopyPrint />
      )}
    </div>
  );
}
